import { useState, useEffect } from 'react';
import { cachedFetchJSON, GEO_TTL } from '../utils/fetchCache';

// Urban Institute Education Data Portal (wraps NCES CCD data)
// Documentation: https://educationdata.urban.org/documentation
// School district endpoints:
//   Directory (enrollment, grad rates, demographics):
//     GET /school-districts/ccd/directory/{year}/?county_fips={5-digit}&per_page=100
//   Finance (expenditures, revenue):
//     GET /school-districts/ccd/finance/{year}/?county_fips={5-digit}&per_page=100
//
// Field names to verify against live API if data looks wrong:
//   grad_rate_adj_cohort_4yr — 4-year adjusted cohort graduation rate
//   frl_pct                  — % students eligible for free/reduced lunch
//   enrollment               — total K-12 enrollment
//   exp_total_per_pup        — total per-pupil expenditure (finance endpoint)
//
// Note: 2020 data is often absent in CCD (COVID disruption) — gracefully handled below.
const EDU_BASE = 'https://educationdata.urban.org/api/v1';

async function fetchDistrictEndpoint(endpoint, year, countyFips5, extraField) {
  // Always request leaid, lea_name, enrollment plus the indicator field
  const fields = ['leaid', 'lea_name', 'enrollment', extraField].filter(Boolean).join(',');
  const url = `${EDU_BASE}/school-districts/ccd/${endpoint}/${year}/?county_fips=${countyFips5}&per_page=100&fields=${fields}`;
  try {
    const data = await cachedFetchJSON(url, GEO_TTL);
    return Array.isArray(data?.results) ? data.results : [];
  } catch (_) {
    return [];
  }
}

function computeCountyValue(districts, field, unit) {
  const valid = districts.filter(d => d[field] != null);
  if (!valid.length) return null;
  const vals = valid.map(d => parseFloat(d[field])).filter(v => !isNaN(v));
  if (!vals.length) return null;

  if (unit === 'count') {
    return vals.reduce((s, v) => s + v, 0);
  }
  // Enrollment-weighted average for rate/percent/dollar metrics
  const enrollWeighted = valid.filter(d => (d.enrollment ?? 0) > 0);
  if (enrollWeighted.length > 0) {
    const totalEnroll = enrollWeighted.reduce((s, d) => s + d.enrollment, 0);
    if (totalEnroll > 0) {
      return enrollWeighted.reduce((s, d) => s + (parseFloat(d[field]) || 0) * d.enrollment, 0) / totalEnroll;
    }
  }
  return vals.reduce((s, v) => s + v, 0) / vals.length;
}

/**
 * Hook: fetches school district data from Urban Institute Education Data Portal.
 *
 * Geography entries use 7-digit LEAIDs as GEOIDs, which triggers the district
 * choropleth rendering path in MapView (instead of the standard tract choropleth).
 *
 * stateValue is not computed — fetching all districts statewide is too expensive
 * for realtime; stateValue is null for education indicators.
 */
export function useEducationData(config, overrideIndicators, geographyOverride) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const indicators = (overrideIndicators ?? config.indicators).filter(i => i.source === 'education');
    if (indicators.length === 0) { setData(new Map()); setLoading(false); return; }

    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const { state: stateFips, county: countyFips } = config.org.geography;
        // Urban Institute API uses zero-padded 5-digit county FIPS
        const countyFips5 = `${stateFips}${countyFips}`.padStart(5, '0');
        const results = new Map();

        await Promise.all(indicators.map(async indicator => {
          const { educationEndpoint, educationField } = indicator;
          const years = indicator.years ?? [2016, 2017, 2018, 2019, 2021, 2022];
          const latestYear = years[years.length - 1];

          const yearData = await Promise.all(years.map(async year => {
            const districts = await fetchDistrictEndpoint(educationEndpoint, year, countyFips5, educationField);
            if (!districts.length) return { year, districts: [], countyVal: null };
            const countyVal = computeCountyValue(districts, educationField, indicator.unit);
            return { year, districts, countyVal };
          }));

          // Use the latest year that has actual data for the primary geography
          const latestWithData = [...yearData].reverse().find(d => d.districts.length > 0);
          const geography = (latestWithData?.districts ?? [])
            .filter(d => d[educationField] != null && d.leaid)
            .map(d => ({
              geoid: String(d.leaid).padStart(7, '0'),
              name: d.lea_name ?? `District ${d.leaid}`,
              value: parseFloat(d[educationField]) || null
            }))
            .filter(g => g.value !== null);

          const geographyByYear = new Map(
            yearData
              .filter(d => d.districts.length > 0)
              .map(({ year, districts }) => [
                year,
                districts
                  .filter(d => d[educationField] != null && d.leaid)
                  .map(d => ({
                    geoid: String(d.leaid).padStart(7, '0'),
                    name: d.lea_name ?? `District ${d.leaid}`,
                    value: parseFloat(d[educationField]) || null
                  }))
                  .filter(g => g.value !== null)
              ])
          );

          const trend = yearData
            .filter(d => d.countyVal !== null)
            .map(({ year, countyVal }) => ({
              year,
              value: Math.round(countyVal * 100) / 100
            }));

          const countyValue =
            trend.find(p => p.year === latestYear)?.value ??
            trend[trend.length - 1]?.value ??
            null;

          results.set(indicator.id, {
            indicatorId: indicator.id,
            label: indicator.label,
            unit: indicator.unit,
            geography,
            geographyByYear,
            stateGeography: [],
            trend,
            stateTrend: [],
            countyValue,
            stateValue: null
          });
        }));

        if (!cancelled) { setData(results); setLoading(false); }
      } catch (err) {
        if (!cancelled) {
          setError(`Education data failed to load: ${err.message}`);
          setLoading(false);
        }
      }
    }

    load();
    return () => { cancelled = true; };
  }, [config, overrideIndicators, geographyOverride]); // eslint-disable-line react-hooks/exhaustive-deps

  return { data, loading, error };
}
